/**
 * Step 3: Model Selection with Recommendations
 */

import { useState, useEffect } from 'react';
import { useWizardState } from '@/hooks/useWizardState';
import { useQuery } from '@tanstack/react-query';
import { supabase as supabaseClient } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Sparkles, Grid3x3 } from 'lucide-react';
import { ModelRecommendationsPanel } from './ModelRecommendationsPanel';
import { ModelScoreboard } from './ModelScoreboard';
import { aiServiceModelPresets } from '@/lib/aiServiceModels';
import { useModelRecommendations } from '@/hooks/useModelRecommendations';
import { toast } from '@/hooks/use-toast';

const supabase = supabaseClient as any;

type Model = {
  model_id: string;
  display_name: string;
  family: string;
  params_b: number;
  max_seq_len: number;
  license_type?: string;
  modality?: string;
  environment_tag?: string;
  category?: string;
  provider?: string;
  benchmarks?: {
    mmlu?: number;
    humaneval?: number;
    gpqa?: number;
    arena_elo?: number;
  };
};

export function ModelSelectionStep() {
  const { state, toggleModelSelection, updateModelSelections } = useWizardState();
  const { getRecommendations } = useModelRecommendations();
  const [searchQuery, setSearchQuery] = useState('');
  const [scoredModels, setScoredModels] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    cloudFriendly: false,
    onPremCapable: false,
    hybrid: false,
    slm: false,
    midLlm: false,
    largeLlm: false,
  });

  const { data: models = [], isLoading } = useQuery({
    queryKey: ['models'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('model_catalog_models')
        .select('*')
        .order('params_b', { ascending: true });
      
      if (error) throw error;
      
      // Parse benchmarks from JSONB column if present
      return (data?.map(model => ({
        ...model,
        benchmarks: model.benchmarks ? {
          mmlu: model.benchmarks.mmlu_score,
          humaneval: model.benchmarks.humaneval_score,
          gpqa: model.benchmarks.gpqa_score,
          arena_elo: model.benchmarks.arena_elo
        } : undefined
      })) || []) as Model[];
    },
  });

  // Score models when they load or personas change
  useEffect(() => {
    if (models.length > 0 && state.personas.length > 0) {
      getRecommendations(
        models.map(m => ({
          model_id: m.model_id,
          display_name: m.display_name,
          params_b: m.params_b,
          family: m.family,
          meta: { category: m.category, modality: m.modality },
          benchmarks: m.benchmarks,
        })),
        state.environment,
        state.personas
      ).then(result => {
        setScoredModels(result.recommendations);
      }).catch(err => {
        console.error('Failed to score models:', err);
      });
    }
  }, [models, state.personas, state.environment, getRecommendations]);

  const filteredModels = models.filter((model) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        model.display_name.toLowerCase().includes(query) ||
        model.family.toLowerCase().includes(query) ||
        (model.provider && model.provider.toLowerCase().includes(query));
      if (!matchesSearch) return false;
    }

    // Environment filters
    const envFiltersActive = filters.cloudFriendly || filters.onPremCapable || filters.hybrid;
    if (envFiltersActive) {
      const envTag = model.environment_tag || 'hybrid';
      if (filters.cloudFriendly && envTag !== 'cloud-preferred') return false;
      if (filters.onPremCapable && envTag !== 'on-prem-friendly') return false;
      if (filters.hybrid && envTag !== 'hybrid') return false;
    }

    // Size filters
    const sizeFiltersActive = filters.slm || filters.midLlm || filters.largeLlm;
    if (sizeFiltersActive) {
      const category = model.category || 'mid-llm';
      if (filters.slm && category !== 'slm') return false;
      if (filters.midLlm && category !== 'mid-llm') return false;
      if (filters.largeLlm && category !== 'large-llm') return false;
    }

    return true;
  });

  const selectedCount = state.modelSelections.selectedModelIds.length;

  const handleQuickSelectTop3 = (recommendations: Array<{ modelId: string }>) => {
    const top3 = recommendations.slice(0, 3).map(r => r.modelId);
    top3.forEach(modelId => {
      if (!state.modelSelections.selectedModelIds.includes(modelId)) {
        toggleModelSelection(modelId);
      }
    });
  };

  const handleClearSelection = () => {
    updateModelSelections({ selectedModelIds: [], personaToModels: {}, modelToPersonas: {} });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-3 text-muted-foreground">Loading models...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selection Actions */}
      {selectedCount > 0 && (
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <span className="text-sm font-medium">
            {selectedCount} model{selectedCount !== 1 ? 's' : ''} selected
          </span>
          <Button variant="outline" size="sm" onClick={handleClearSelection}>
            Clear Selection
          </Button>
        </div>
      )}

      <Tabs defaultValue={state.personas.length > 0 ? "recommended" : "all"} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="recommended" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Recommended
            {selectedCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {selectedCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <Grid3x3 className="h-4 w-4" />
            All Models
            {selectedCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {selectedCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Recommended Models Tab */}
        <TabsContent value="recommended" className="space-y-6 mt-6">
          {state.personas.length > 0 ? (
            <ModelScoreboard
              onPremModels={models
                .map(m => {
                  const score = scoredModels.find(s => s.modelId === m.model_id);
                  return score ? {
                    ...score,
                    display_name: m.display_name,
                    params_b: m.params_b,
                    family: m.family,
                  } : null;
                })
                .filter((m): m is NonNullable<typeof m> => m !== null)}
              cloudModels={aiServiceModelPresets}
              selectedModelIds={state.modelSelections.selectedModelIds}
              onSelectModel={toggleModelSelection}
              personas={state.personas}
              environment={state.environment}
            />
          ) : (
            <Card className="p-8 text-center">
              <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No Personas Yet</h3>
              <p className="text-sm text-muted-foreground">
                Add personas in the previous step to get AI-powered model recommendations.
              </p>
            </Card>
          )}
        </TabsContent>

        {/* All Models Tab */}
        <TabsContent value="all" className="space-y-6 mt-6">
          {/* Filters */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search models..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Environment</Label>
            <div className="flex gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="cloud"
                  checked={filters.cloudFriendly}
                  onCheckedChange={(checked) =>
                    setFilters({ ...filters, cloudFriendly: !!checked })
                  }
                />
                <Label htmlFor="cloud" className="font-normal cursor-pointer">
                  Cloud
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="on-prem"
                  checked={filters.onPremCapable}
                  onCheckedChange={(checked) =>
                    setFilters({ ...filters, onPremCapable: !!checked })
                  }
                />
                <Label htmlFor="on-prem" className="font-normal cursor-pointer">
                  On-Prem
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hybrid-filter"
                  checked={filters.hybrid}
                  onCheckedChange={(checked) =>
                    setFilters({ ...filters, hybrid: !!checked })
                  }
                />
                <Label htmlFor="hybrid-filter" className="font-normal cursor-pointer">
                  Hybrid
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Model Size</Label>
            <div className="flex gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="slm"
                  checked={filters.slm}
                  onCheckedChange={(checked) => setFilters({ ...filters, slm: !!checked })}
                />
                <Label htmlFor="slm" className="font-normal cursor-pointer">
                  SLM (&lt;20B)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="mid"
                  checked={filters.midLlm}
                  onCheckedChange={(checked) => setFilters({ ...filters, midLlm: !!checked })}
                />
                <Label htmlFor="mid" className="font-normal cursor-pointer">
                  Mid (20-50B)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="large"
                  checked={filters.largeLlm}
                  onCheckedChange={(checked) =>
                    setFilters({ ...filters, largeLlm: !!checked })
                  }
                />
                <Label htmlFor="large" className="font-normal cursor-pointer">
                  Large (50B+)
                </Label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Models Summary */}
      {selectedCount > 0 && (
        <Card className="p-3 bg-muted/50">
          <span className="text-sm font-medium">{selectedCount} model{selectedCount !== 1 ? 's' : ''} selected</span>
        </Card>
      )}

      {/* Models Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredModels.map((model) => {
          const isSelected = state.modelSelections.selectedModelIds.includes(model.model_id);
          
          return (
            <Card
              key={model.model_id}
              className={`p-4 cursor-pointer transition-all ${
                isSelected ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => toggleModelSelection(model.model_id)}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold">{model.display_name}</h4>
                  {model.provider && (
                    <p className="text-sm text-muted-foreground">{model.provider}</p>
                  )}
                </div>
                <Checkbox 
                  checked={isSelected} 
                  onCheckedChange={() => {}} 
                  className="pointer-events-none" 
                />
              </div>

              <div className="flex flex-wrap gap-1 mb-3">
                <Badge variant="outline">{model.params_b}B params</Badge>
                {model.category && (
                  <Badge variant="secondary">{model.category}</Badge>
                )}
                {model.environment_tag && (
                  <Badge
                    className={
                      model.environment_tag === 'on-prem-friendly'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100'
                        : model.environment_tag === 'cloud-preferred'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100'
                        : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100'
                    }
                  >
                    {model.environment_tag}
                  </Badge>
                )}
              </div>

              <div className="text-xs text-muted-foreground space-y-1">
                <div>Context: {(model.max_seq_len / 1000).toFixed(0)}K tokens</div>
                {model.license_type && (
                  <div>License: {model.license_type}</div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

          {filteredModels.length === 0 && (
            <Card className="p-12 text-center">
              <p className="text-muted-foreground">No models match your filters</p>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
